// src/pages/BillingPage.js
// Company admin billing page — view plan, pay via Stripe, see invoices
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { billingAPI } from '../services/api';
import { Card, Badge, SectionHeader, Button, EmptyState } from '../components/ui';
import toast from 'react-hot-toast';
import { CreditCard, CheckCircle, AlertTriangle, ExternalLink, Receipt } from 'lucide-react';
import styles from './BillingPage.module.css';

const PLANS = [
    {
        key: 'starter',
        name: 'Starter',
        price: 399,
        warehouses: 1,
        tags: '500/mo',
        ai: '50K tokens/mo',
        highlight: false,
    },
    {
        key: 'growth',
        name: 'Growth',
        price: 799,
        warehouses: 3,
        tags: '2,000/mo',
        ai: '200K tokens/mo',
        highlight: true,
    },
    {
        key: 'enterprise',
        name: 'Enterprise',
        price: 1499,
        warehouses: 'Unlimited',
        tags: 'Unlimited',
        ai: '1M tokens/mo',
        highlight: false,
    },
];

export default function BillingPage() {
    const { companyId, refreshSubscription } = useAuth();
    const [sub, setSub] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(null);
    const [portalLoading, setPortalLoading] = useState(false);

    // If user just came back from Stripe success page, refresh subscription in context
    useEffect(() => {
        if (window.location.search.includes('session_id')) {
            refreshSubscription();
        }
    }, []);

    const load = useCallback(async () => {
        if (!companyId) return;
        setLoading(true);
        try {
            const [subRes, invRes] = await Promise.all([
                billingAPI.getSubscription({ companyId }),
                billingAPI.getInvoices({ companyId }),
            ]);
            setSub(subRes.data);
            setInvoices(invRes.data.invoices || []);
        } catch (_) { }
        setLoading(false);
    }, [companyId]);

    useEffect(() => { load(); }, [load]);

    const handleCheckout = async (plan) => {
        setCheckoutLoading(plan);
        try {
            const res = await billingAPI.checkout({ companyId, plan });
            window.location.href = res.data.url;
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to start checkout');
        }
        setCheckoutLoading(null);
    };

    const handlePortal = async () => {
        setPortalLoading(true);
        try {
            const res = await billingAPI.portal({ companyId });
            window.open(res.data.url, '_blank');
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to open billing portal');
        }
        setPortalLoading(false);
    };

    const currentPlan = sub?.plan || null;
    const subStatus = sub?.status || 'none';
    const isActive = subStatus === 'active';
    const isPastDue = subStatus === 'past_due';
    const isExpired = sub?.isExpired;

    return (
        <div className={`${styles.page} animate-fadeIn`}>
            <SectionHeader
                title="Billing & Subscription"
                description="Manage your GAAFrfid plan and payment method"
            />

            {/* Status banner */}
            {(isPastDue || isExpired) && (
                <div className={styles.alertBanner}>
                    <AlertTriangle size={18} />
                    <div>
                        <strong>Payment Past Due</strong>
                        <p>Your Subscription Payment is Overdue. Some features may be restricted. Please contact <a href="mailto:rfidsales@gaaftechnologies.com">rfidsales@gaaftechnologies.com</a> or subscribe below.</p>
                    </div>
                </div>
            )}

            {/* Current plan summary */}
            {sub && isActive && (
                <Card className={styles.currentPlan}>
                    <div className={styles.currentPlanHeader}>
                        <CheckCircle size={20} className={styles.activeIcon} />
                        <div>
                            <div className={styles.currentPlanName}>{sub.planName} Plan — Active</div>
                            <div className={styles.currentPlanSub}>
                                {sub.source === 'stripe'
                                    ? 'Managed via Stripe — use Manage Subscription to update card or cancel'
                                    : sub.paidUntil
                                        ? `Paid until ${new Date(sub.paidUntil).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                                        : 'Manual payment recorded by GAAF'}
                            </div>
                        </div>
                        {sub.hasStripe && (
                            <Button variant="secondary" size="sm" icon={<ExternalLink size={13} />}
                                loading={portalLoading} onClick={handlePortal}>
                                Manage Subscription
                            </Button>
                        )}
                    </div>
                    <div className={styles.usageRow}>
                        <div className={styles.usageStat}>
                            <span className={styles.usageLabel}>Warehouses</span>
                            <span className={styles.usageValue}>{sub.maxWarehouses === 999 ? 'Unlimited' : `Up to ${sub.maxWarehouses}`}</span>
                        </div>
                        <div className={styles.usageStat}>
                            <span className={styles.usageLabel}>AI Tokens/mo</span>
                            <span className={styles.usageValue}>{sub.aiTokensPerMonth >= 1000000 ? 'Unlimited' : (sub.aiTokensPerMonth / 1000).toFixed(0) + 'K'}</span>
                        </div>
                        <div className={styles.usageStat}>
                            <span className={styles.usageLabel}>Monthly Price</span>
                            <span className={styles.usageValue}>${sub.priceMonthly}/mo</span>
                        </div>
                    </div>
                </Card>
            )}

            {/* Plan picker */}
            <div className={styles.plansSection}>
                <h2 className={styles.sectionTitle}>
                    {isActive ? 'Change Plan' : 'Choose a Plan'}
                </h2>
                <div className={styles.plansGrid}>
                    {PLANS.map(plan => {
                        const isCurrent = plan.key === currentPlan;
                        return (
                            <div key={plan.key}
                                className={`${styles.planCard} ${plan.highlight ? styles.planCardHighlight : ''} ${isCurrent ? styles.planCardCurrent : ''}`}>
                                {plan.highlight && <div className={styles.planBadge}>Most Popular</div>}
                                {isCurrent && <div className={styles.currentBadge}>Current Plan</div>}
                                <div className={styles.planName}>{plan.name}</div>
                                <div className={styles.planPrice}>
                                    <span className={styles.planAmount}>${plan.price}</span>
                                    <span className={styles.planPer}>/mo</span>
                                </div>
                                <ul className={styles.planFeatures}>
                                    <li>{plan.warehouses === 'Unlimited' ? 'Unlimited' : `${plan.warehouses}`} Warehouse{plan.warehouses !== 1 && plan.warehouses !== 'Unlimited' ? 's' : ''}</li>
                                    <li>{plan.tags} Tag Events</li>
                                    <li>{plan.ai} AI Usage</li>
                                    <li>Dashboard + Mobile App</li>
                                    <li>RFID Staff Attendance</li>
                                    <li>Email Alerts</li>
                                </ul>
                                <Button
                                    variant={isCurrent ? 'secondary' : plan.highlight ? 'primary' : 'secondary'}
                                    fullWidth
                                    disabled={isCurrent}
                                    loading={checkoutLoading === plan.key}
                                    onClick={() => handleCheckout(plan.key)}
                                >
                                    {isCurrent ? 'Current Plan' : isActive ? 'Switch to ' + plan.name : 'Subscribe'}
                                </Button>
                            </div>
                        );
                    })}
                </div>
                <p className={styles.wireNote}>
                    Prefer to Pay by Wire Transfer or Check? Contact us at <a href="mailto:rfidsales@gaaftechnologies.com">rfidsales@gaaftechnologies.com</a> — we'll activate your plan manually.
                </p>
            </div>

            {/* Invoice history */}
            {invoices.length > 0 && (
                <Card className={styles.invoicesSection}>
                    <div className={styles.invoicesHeader}>
                        <Receipt size={16} />
                        <h3>Payment History</h3>
                    </div>
                    <div className={styles.invoicesList}>
                        {invoices.slice(0, 12).map(inv => (
                            <div key={inv.billingId} className={styles.invoiceRow}>
                                <div className={styles.invoiceLeft}>
                                    <span className={styles.invoiceDate}>
                                        {new Date(inv.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </span>
                                    <span className={styles.invoiceDesc}>
                                        {inv.source === 'manual' ? 'Manual Payment' : `Invoice ${inv.invoiceNumber || inv.billingId?.slice(-8)}`}
                                    </span>
                                </div>
                                <div className={styles.invoiceRight}>
                                    {inv.amountPaid > 0 && (
                                        <span className={styles.invoiceAmount}>${(inv.amountPaid / 100).toFixed(2)}</span>
                                    )}
                                    <Badge variant={inv.paid || inv.status === 'active' ? 'success' : 'danger'}>
                                        {inv.paid ? 'Paid' : inv.status}
                                    </Badge>
                                    {inv.invoiceUrl && (
                                        <a href={inv.invoiceUrl} target="_blank" rel="noopener noreferrer" className={styles.invoiceLink}>
                                            <ExternalLink size={13} />
                                        </a>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}
        </div>
    );
}
